/**
 * A component that renders the content of an accepted clarification discussion post.
 *
 * These types of posts only appear on video "Mistakes" tabs.
 *
 * IMPORTANT: Unaccepted clarifications are rendered in a different file
 * (at the time of writing it's discussion-post.jsx).
 * We need to keep this file in sync with that one.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";
import {connect} from "react-redux";

import {View} from "@khanacademy/wonder-blocks-core";
import {ActionItem, ActionMenu} from "@khanacademy/wonder-blocks-dropdown";
import {Body, LabelMedium} from "@khanacademy/wonder-blocks-typography";

import type {OfficialClarification} from "#clarifications-package/types.ts";
import {
    getTimestampFromSeconds,
    VideoTimestampLink,
} from "#components/formatted-markdown-package/format-content.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedSpring from "#components/layout-package/deprecated-spring.tsx";
import type {WithApolloProps} from "#data-access/gql/with-apollo.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import * as sharedStyles from "../shared-styles.ts";
import type {PostKey} from "../types.ts";
import {PostAuthorName} from "../util.tsx";

import AcceptedClarificationInput from "./accepted-clarification-input.tsx";
import {
    deleteAcceptedClarificationPost,
    startEditingPostAction,
    stopEditingPostAction,
} from "./discussion-actions.ts";
import DiscussionAvatar from "./discussion-avatar.tsx";
import type {State as ReducerState} from "./discussion-reducer.ts";

type OwnProps = {
    post: OfficialClarification;
};

type ReduxProps = {
    postBeingEditedKey: PostKey | null | undefined;
    onDeleteAcceptedClarificationPost: (post: OfficialClarification) => void;
    onStartEditingPost: (postKey: string) => void;
    onStopEditingPost: () => void;
};

type Props = OwnProps & ReduxProps & WithApolloProps & WithUserProfileProps;

export class DiscussionPostAcceptedClarification extends React.Component<Props> {
    // Render "edit" and "delete" buttons when logged in as a moderator.
    renderModeratorActions(
        profile: ProfileModelType | null | undefined,
        style: any,
    ): React.ReactNode {
        if (!profile || !profile.get("isModerator")) {
            return null;
        }

        const {onDeleteAcceptedClarificationPost, onStartEditingPost, post} =
            this.props;

        const items = [
            <ActionItem
                key="edit"
                label={t`Edit`}
                onClick={() => onStartEditingPost(post.id)}
            />,
            <ActionItem
                key="delete"
                label={t`Delete`}
                onClick={() => onDeleteAcceptedClarificationPost(post)}
            />,
        ];

        return (
            <ActionMenu menuText="more" alignment="right" style={style}>
                {items}
            </ActionMenu>
        );
    }

    renderPostHeader: () => React.ReactNode = () => {
        const {post, profile} = this.props;
        const {reporter, openTimestamp, closeTimestamp} = post;
        const {nickname, kaid} = reporter || {};

        const openTimestampObject = getTimestampFromSeconds(openTimestamp);
        const closeTimestampObject = getTimestampFromSeconds(closeTimestamp);
        const openTimestampReadable = `${openTimestampObject.minutes}:${openTimestampObject.seconds}`;
        const closeTimestampReadable = `${closeTimestampObject.minutes}:${closeTimestampObject.seconds}`;
        const timestampLinkContent = t`Appears at ${openTimestampReadable} - ${closeTimestampReadable}`;

        return (
            <View style={sharedStyles.postHeader}>
                <View style={sharedStyles.nameAndDatetime}>
                    <PostAuthorName name={nickname ?? ""} kaid={kaid} />

                    <LabelMedium
                        style={[
                            sharedStyles.datetime,
                            styles.videoTimestampLink,
                        ]}
                    >
                        <VideoTimestampLink
                            timestamp={openTimestampReadable}
                            minutes={openTimestampObject.minutes.toString()}
                            seconds={openTimestampObject.seconds.toString()}
                            children={timestampLinkContent}
                        />
                    </LabelMedium>
                </View>
                {this.renderModeratorActions(
                    profile,
                    sharedStyles.actionPickerMobile,
                )}
            </View>
        );
    };

    render(): React.ReactNode {
        const {
            post,
            postBeingEditedKey,
            profile,
            onStopEditingPost,
            gqlClient,
        } = this.props;
        const {nickname, avatarSrc, kaid} = post.reporter || {};

        const editing = post.id === postBeingEditedKey;

        return (
            <View testId="discussion-post">
                {!editing && (
                    <View style={sharedStyles.post}>
                        <DiscussionAvatar
                            username={nickname}
                            avatarName={null}
                            avatarSrc={avatarSrc}
                            kaid={kaid}
                            enableProfilePreviewTooltip={true}
                        />

                        <View style={sharedStyles.everythingButTheAvatar}>
                            {this.renderPostHeader()}

                            <View style={sharedStyles.contentContainer}>
                                <Body style={sharedStyles.content}>
                                    <View>{post.text}</View>
                                </Body>
                            </View>

                            <View style={sharedStyles.postFooter}>
                                <DeprecatedSpring />
                                <View style={sharedStyles.tools}>
                                    {this.renderModeratorActions(
                                        profile,
                                        sharedStyles.actionPickerDesktop,
                                    )}
                                </View>
                            </View>
                        </View>
                    </View>
                )}
                {editing && (
                    // @ts-expect-error - TS2741 - Property 'topicSlug' is missing in type '{ post: OfficialClarification; editingOrAccepting: "editing"; onCancel: () => void; }' but required in type 'ExportProps'.
                    <AcceptedClarificationInput
                        post={post}
                        editingOrAccepting="editing"
                        onCancel={onStopEditingPost}
                        gqlClient={gqlClient}
                    />
                )}
            </View>
        );
    }
}

const styles = StyleSheet.create({
    videoTimestampLink: {
        // We're reusing a vanilla css class for the video timestamp links,
        // so we're adding this hacky styling here to get the text to line up correctly
        // without messing up the other css file.
        marginBottom: -2,
    },
});

const mapDispatchToProps = {
    onDeleteAcceptedClarificationPost: deleteAcceptedClarificationPost,
    onStartEditingPost: startEditingPostAction,
    onStopEditingPost: stopEditingPostAction,
} as const;

const mapStateToProps = (state: ReducerState) => {
    const {postBeingEditedKey} = state;

    return {postBeingEditedKey};
};

type ExportProps = WithoutUserProfile<
    Without<
        JSX.LibraryManagedAttributes<
            typeof DiscussionPostAcceptedClarification,
            React.ComponentProps<typeof DiscussionPostAcceptedClarification>
        >,
        ReduxProps
    >
>;

export default compose(
    withUserProfile,
    connect(mapStateToProps, mapDispatchToProps),
)(DiscussionPostAcceptedClarification) as React.ComponentType<ExportProps>;
